import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useExpiredCapabilityOverrides,
  useOrphanCapabilityOverrides,
  usePurgeExpiredCapabilityOverrides,
} from "@/hooks/useCapabilities";
import type { CapabilityOverride } from "@/services/capabilitiesApi";

function OverrideRow({ override }: { override: CapabilityOverride }) {
  return (
    <TableRow>
      <TableCell className="font-mono text-sm">{override.capability}</TableCell>
      <TableCell className="text-sm">
        {override.scope_type}
        {override.scope_id ? ` (${override.scope_id})` : ""}
      </TableCell>
      <TableCell>
        <Badge variant={override.state === "allow" ? "default" : "destructive"}>
          {override.state}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {override.reason}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {override.expires_at ?? "—"}
      </TableCell>
    </TableRow>
  );
}

export function OrphansExpiredPanel() {
  const orphansQuery = useOrphanCapabilityOverrides();
  const expiredQuery = useExpiredCapabilityOverrides();
  const purgeMutation = usePurgeExpiredCapabilityOverrides();

  const handlePurge = async () => {
    try {
      const result = await purgeMutation.mutateAsync();
      toast.success(`Purged ${result.purged} expired override(s)`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to purge");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Orphaned overrides</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-muted-foreground">
            Rows whose capability no longer exists in the registry — usually
            left behind after a capability was renamed or removed.
          </p>
          {orphansQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : orphansQuery.data && orphansQuery.data.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Capability</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Expires</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphansQuery.data.map((o) => (
                  <OverrideRow key={o.id} override={o} />
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No orphaned rows.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Expired overrides</CardTitle>
          {expiredQuery.data && expiredQuery.data.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handlePurge()}
              disabled={purgeMutation.isPending}
            >
              {purgeMutation.isPending && (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              )}
              Purge expired
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {expiredQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : expiredQuery.data && expiredQuery.data.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Capability</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Expired</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expiredQuery.data.map((o) => (
                  <OverrideRow key={o.id} override={o} />
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No expired rows.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
